import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { WireframeBackground } from '@/components/WireframeBackground';

// ---------------------------------------------------------------------------
// Terms of Service — Meshpit
// Last updated: 2026-04-08
// ---------------------------------------------------------------------------

const EFFECTIVE_DATE = '8 April 2026';
// TODO: Replace with the actual governing jurisdiction before going live
const GOVERNING_LAW = 'Switzerland';

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

const SECTIONS: Section[] = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: (
      <>
        <p>
          By accessing or using Meshpit (the "Service", "Platform"), you agree
          to be bound by these Terms of Service ("Terms") and our Privacy
          Policy. If you do not agree to these Terms, do not use the Service.
        </p>
        <p>
          These Terms constitute a legally binding agreement between you and the
          operator of Meshpit ("we", "us", "our"). We may update these Terms
          from time to time; continued use of the Service after an update
          constitutes acceptance of the revised Terms. We will provide
          reasonable notice of material changes.
        </p>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: '2. Eligibility',
    content: (
      <>
        <p>
          You must be at least 18 years of age to use the Service. If you are
          between 13 and 17, you may only use the Service with the verifiable
          consent and active supervision of a parent or legal guardian who
          agrees to these Terms on your behalf.
        </p>
        <p>
          By using the Service you represent and warrant that you have the legal
          right and capacity to enter into this agreement in your jurisdiction,
          and that your use of the Service does not violate any applicable law
          or regulation.
        </p>
      </>
    ),
  },
  {
    id: 'user-content',
    title: '3. User Content & Licences',
    content: (
      <>
        <p>
          You retain ownership of all models, code, images, and other content
          you upload to the Service ("User Content"). By uploading User Content
          you grant Meshpit a worldwide, non-exclusive, royalty-free licence to
          host, store, display, distribute, and transmit that content solely for
          the purposes of operating and improving the Service.
        </p>
        <p>
          You are solely responsible for your User Content. You represent and
          warrant that: (a) you own or have obtained all necessary rights,
          licences, and permissions to upload and (where applicable) sell the
          content; (b) your User Content does not infringe the intellectual
          property, privacy, or other rights of any third party; and (c) your
          User Content complies with these Terms and all applicable laws.
        </p>
        <p>
          If you publish a model for sale, you grant buyers a non-exclusive,
          non-transferable personal licence to use the model files for their own
          personal or commercial manufacturing purposes, unless you explicitly
          state otherwise in your model description. Resale or redistribution of
          model files by buyers is not permitted by default.
        </p>
      </>
    ),
  },
  {
    id: 'prohibited',
    title: '4. Prohibited Content',
    content: (
      <>
        <p>
          The following content is strictly prohibited on Meshpit. Uploading,
          publishing, selling, or otherwise distributing any of the following
          may result in immediate account termination and, where required by
          law, reporting to relevant authorities:
        </p>
        <ul>
          <li>
            <strong>Copyrighted works without authorisation.</strong> Models,
            designs, or derivative works that reproduce protected intellectual
            property — including trademarked characters, patented designs,
            commercially licensed assets, or any content you do not own or do
            not hold a valid licence to sell or distribute.
          </li>
          <li>
            <strong>Functional weapons and firearm components.</strong> Models
            designed or optimised to function as firearms, firearm receivers,
            conversion kits, suppressors, or any component whose primary or
            intended purpose is to cause physical harm to people or animals.
            This includes, but is not limited to, ghost-gun components, 3D
            printable AR lowers, Glock switches, and similar items, regardless
            of whether manufacture is legal in your jurisdiction.
          </li>
          <li>
            <strong>Content that facilitates illegal activity.</strong> Models,
            tools, or instructions designed to assist in committing crimes,
            including but not limited to lock-bypassing devices, drug
            paraphernalia designed to conceal controlled substances, skimming
            devices, and surveillance equipment intended for illegal use.
          </li>
          <li>
            <strong>
              Child sexual abuse material (CSAM) and sexualised minors.
            </strong>{' '}
            Any sexually explicit content depicting, or intended to depict,
            minors is absolutely prohibited and will be reported to the relevant
            authorities and NCMEC (or equivalent national body) without
            exception.
          </li>
          <li>
            <strong>Malware and destructive code.</strong> OpenSCAD scripts or
            any files intentionally crafted to exploit vulnerabilities, cause
            data loss, or compromise the systems of other users or the platform.
          </li>
          <li>
            <strong>Hateful or harassing content.</strong> Content that
            promotes, glorifies, or is designed to incite violence or
            discrimination against individuals or groups based on race,
            ethnicity, religion, gender, sexual orientation, disability, or
            national origin.
          </li>
        </ul>
        <p>
          We reserve the right to determine, at our sole discretion, whether
          content violates this policy. If you believe a model has been removed
          in error, please contact us via the footer contact form.
        </p>
      </>
    ),
  },
  {
    id: 'copyright-dmca',
    title: '5. Copyright & DMCA',
    content: (
      <>
        <p>
          Meshpit respects intellectual property rights and expects users to do
          the same. We comply with the Digital Millennium Copyright Act (DMCA)
          and equivalent international copyright laws.
        </p>
        <p>
          <strong>Takedown requests.</strong> If you believe that content on
          Meshpit infringes your copyright, please send a written notice
          containing: (a) identification of the copyrighted work claimed to be
          infringed; (b) identification of the infringing material and its
          location on the platform; (c) your contact information; (d) a
          statement that you have a good-faith belief that the use is not
          authorised; (e) a statement under penalty of perjury that the
          information is accurate; and (f) your physical or electronic
          signature. Send notices via the contact form in the site footer.
        </p>
        <p>
          <strong>Counter-notices.</strong> If you believe your content was
          removed as a result of a mistake or misidentification, you may submit
          a counter-notice. Upon receipt of a valid counter-notice, we will
          restore the content unless the reporting party initiates a court
          action within the statutory period.
        </p>
        <p>
          <strong>Repeat infringers.</strong> Meshpit will terminate the
          accounts of users who are determined to be repeat copyright
          infringers.
        </p>
        <p>
          Meshpit is a hosting platform and is not liable for User Content
          uploaded by third parties. We act in good faith upon valid takedown
          notices.
        </p>
      </>
    ),
  },
  {
    id: 'seller-responsibilities',
    title: '6. Seller Responsibilities',
    content: (
      <>
        <p>
          If you use the Seller features of the Service (paid model publishing),
          you additionally agree to the following:
        </p>
        <ul>
          <li>
            <strong>Accuracy.</strong> Model descriptions, previews, and
            parameter documentation must accurately represent the model's
            capabilities and output. Misleading listings are prohibited.
          </li>
          <li>
            <strong>Functionality.</strong> Paid models must compile and render
            correctly in the latest supported version of Meshpit's OpenSCAD
            runtime at the time of publication. You are responsible for
            maintaining and updating your models if platform changes affect
            compatibility.
          </li>
          <li>
            <strong>Taxes.</strong> You are solely responsible for determining,
            collecting, and remitting any applicable taxes (including VAT, GST,
            or sales tax) on sales made through the platform. Meshpit does not
            collect or remit taxes on your behalf.
          </li>
          <li>
            <strong>Buyer support.</strong> You are responsible for resolving
            reasonable buyer complaints about your models. Persistent failure to
            do so may result in suspension of your Seller privileges.
          </li>
          <li>
            <strong>No fraudulent transactions.</strong> Purchasing your own
            models to inflate sales figures, or any other form of payment
            manipulation, is strictly prohibited and will result in immediate
            account termination and reversal of affected transactions.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'fees-payments',
    title: '7. Platform Fees & Payments',
    content: (
      <>
        <p>
          Selling models on Meshpit requires an active Seller subscription and
          is subject to a platform revenue-share fee. Current rates are
          published on the{' '}
          <Link
            to="/pricing"
            className="font-medium text-foreground underline underline-offset-4 hover:text-muted-foreground"
          >
            Pricing page
          </Link>
          . We reserve the right to update fee structures with at least 30 days'
          advance notice to active Sellers.
        </p>
        <p>
          All payments are processed by Stripe. By using Seller features you
          also agree to{' '}
          <a
            href="https://stripe.com/legal/ssa"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground underline underline-offset-4 hover:text-muted-foreground"
          >
            Stripe's Services Agreement
          </a>
          . Meshpit is not a party to transactions between buyers and sellers
          beyond providing the platform and processing infrastructure.
        </p>
        <p>
          Subscription fees are billed annually and are non-refundable except
          where required by applicable law. If your account is terminated for
          cause under Section 9, no refund of the subscription fee will be
          issued.
        </p>
        <p>
          We reserve the right to withhold or reverse payouts if there is
          reasonable suspicion of fraud, chargebacks, or violation of these
          Terms, pending investigation.
        </p>
      </>
    ),
  },
  {
    id: 'moderation-termination',
    title: '8. Content Moderation & Account Termination',
    content: (
      <>
        <p>Meshpit reserves the right, at our sole discretion, to:</p>
        <ul>
          <li>
            Remove or restrict access to any User Content at any time, with or
            without notice, for any reason, including but not limited to
            violation of these Terms.
          </li>
          <li>
            Suspend or permanently terminate any account that violates these
            Terms, engages in fraudulent activity, or poses a risk to the
            platform or its users.
          </li>
          <li>
            Cooperate fully with law enforcement and provide user data when
            required by valid legal process.
          </li>
        </ul>
        <p>
          If your account is terminated, you may lose access to your models,
          sales history, and all associated data. We are not liable for any loss
          resulting from account termination for cause.
        </p>
        <p>
          You may delete your own account at any time from your account
          settings. Deletion is permanent; we cannot recover deleted accounts or
          their content.
        </p>
      </>
    ),
  },
  {
    id: 'disclaimers',
    title: '9. Disclaimer of Warranties',
    content: (
      <>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
          OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
          IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT.
        </p>
        <p>
          We do not warrant that the Service will be uninterrupted, error-free,
          or free of harmful components, or that any defects will be corrected.
          We do not warrant the accuracy, completeness, or usefulness of any
          User Content available through the Service.
        </p>
        <p>
          Models published on Meshpit are user-generated content. Meshpit does
          not review, endorse, or guarantee the accuracy, safety, or
          print-readiness of any model. You use models at your own risk.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: '10. Limitation of Liability',
    content: (
      <>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, MESHPIT AND ITS
          AFFILIATES, OFFICERS, EMPLOYEES, AND LICENSORS SHALL NOT BE LIABLE FOR
          ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
          OR ANY LOSS OF PROFITS, REVENUE, DATA, BUSINESS, OR GOODWILL, ARISING
          OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE, EVEN IF WE HAVE
          BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
          RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF
          (A) THE AMOUNT YOU PAID TO MESHPIT IN THE TWELVE MONTHS PRECEDING THE
          CLAIM, OR (B) USD 100.
        </p>
      </>
    ),
  },
  {
    id: 'governing-law',
    title: '11. Governing Law & Disputes',
    content: (
      <>
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of <strong>{GOVERNING_LAW}</strong>, without regard to its
          conflict of law provisions.
        </p>
        <p>
          Any dispute arising out of or in connection with these Terms shall
          first be attempted to be resolved through good-faith negotiation. If
          not resolved within 30 days, disputes shall be submitted to the
          competent courts of <strong>{GOVERNING_LAW}</strong>, which shall have
          exclusive jurisdiction.
        </p>
        <p>
          Notwithstanding the foregoing, either party may seek injunctive or
          other equitable relief in any court of competent jurisdiction to
          prevent irreparable harm.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: '12. Changes to These Terms',
    content: (
      <>
        <p>
          We may update these Terms at any time. When we make material changes,
          we will update the "Last updated" date at the top of this page and,
          where appropriate, notify you by email or via an in-app notice.
        </p>
        <p>
          Your continued use of the Service after the effective date of the
          revised Terms constitutes your acceptance of the changes. If you do
          not agree to the new Terms, you must stop using the Service.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: '13. Contact',
    content: (
      <>
        <p>
          If you have questions about these Terms, wish to submit a copyright
          takedown notice, or need to report a violation, please use the contact
          form in the footer of any page on this site.
        </p>
      </>
    ),
  },
];

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function Terms() {
  return (
    <div className="relative min-h-full bg-background">
      <WireframeBackground />
      <div className="relative z-10 mx-auto max-w-3xl px-6 pb-32">
        {/* Header */}
        <div className="py-16">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>

          <p className="mb-4 mt-8 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
            Legal
          </p>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">
            Terms of Service
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Effective date: {EFFECTIVE_DATE}
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Please read these Terms carefully before using Meshpit. They govern
            your use of the platform and include important information about
            your rights and responsibilities, including rules around prohibited
            content, copyright, and selling models.
          </p>

          {/* Jump-to nav */}
          <nav
            className="mt-8 flex flex-wrap gap-2"
            aria-label="Jump to section"
          >
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/20 hover:bg-muted hover:text-foreground"
              >
                {s.title}
              </a>
            ))}
          </nav>
        </div>

        {/* Sections */}
        <div className="flex flex-col divide-y divide-border">
          {SECTIONS.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="scroll-mt-8 py-10"
            >
              <h2 className="mb-5 text-xl font-semibold text-foreground">
                {section.title}
              </h2>
              <div className="prose prose-sm max-w-none dark:prose-invert prose-p:leading-relaxed prose-p:text-muted-foreground prose-a:text-foreground prose-strong:text-foreground prose-li:leading-relaxed prose-li:text-muted-foreground [&_li]:flex [&_li]:gap-2 [&_li]:before:shrink-0 [&_li]:before:text-muted-foreground/50 [&_li]:before:content-['–'] [&_p+p]:mt-4 [&_ul]:mt-4 [&_ul]:flex [&_ul]:list-none [&_ul]:flex-col [&_ul]:gap-3 [&_ul]:pl-0">
                {section.content}
              </div>
            </section>
          ))}
        </div>

        {/* Footer note */}
        <div className="py-10 text-center">
          <p className="text-xs text-muted-foreground/60">
            &copy; 2026 Fredi Bach &nbsp;·&nbsp;{' '}
            <Link
              to="/pricing"
              className="underline-offset-4 hover:text-muted-foreground hover:underline"
            >
              Pricing
            </Link>{' '}
            ·{' '}
            <Link
              to="/"
              className="underline-offset-4 hover:text-muted-foreground hover:underline"
            >
              Home
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
